import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Badge,
  Button,
  Card,
  CardFooter,
  ConfirmDialog,
  SegmentedControl,
  Select,
  getCategoryBadgeVariant,
} from '@/components/ui';
import { useAuth } from '@/lib/auth-context';
import { useBranding } from '@/lib/branding-context';
import { CATEGORY_OPTIONS, getPrayerCategories } from '@/lib/categories';
import { formatFullName } from '@/lib/format';
import { StageQueueItem, useStageQueue } from '@/hooks/useStageQueue';
import { PrayerType } from '@/types';

const STAGE_ROLES = ['moderator', 'producer', 'admin'];
const SORT_STORAGE_KEY = 'stage-sort-order';

type StageSort = 'oldest' | 'newest';
type TypeFilter = 'all' | 'prayer' | 'praise';

function readSort(): StageSort {
  try {
    return localStorage.getItem(SORT_STORAGE_KEY) === 'newest' ? 'newest' : 'oldest';
  } catch {
    return 'oldest';
  }
}

function isPraise(type: PrayerType): boolean {
  return type === PrayerType.PRAISE || type === PrayerType.PRAISE_REPORT;
}

function StageCard({
  item,
  isSending,
  isRemoving,
  onSendToScreen,
  onRemove,
}: {
  item: StageQueueItem;
  isSending: boolean;
  isRemoving: boolean;
  onSendToScreen: () => void;
  onRemove: () => void;
}) {
  const prayer = item.prayer;
  const praise = isPraise(prayer.type);
  const name = prayer.isAnonymous
    ? 'Anonymous'
    : formatFullName(prayer.firstName, prayer.lastName) || 'Anonymous';
  const categories = getPrayerCategories(prayer);
  const categoryLabels = categories.length > 0 ? categories : ['General'];

  return (
    <Card
      padding="md"
      className="transition-all duration-200 hover:border-primary-300 hover:shadow-md dark:hover:border-primary-600"
    >
      <div className="flex h-full flex-col">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Badge variant={praise ? 'amber' : 'sky'}>{praise ? 'Answered Prayer' : 'Prayer'}</Badge>
          {categoryLabels.map((cat) => (
            <Badge key={cat} variant={getCategoryBadgeVariant(cat)} className="capitalize">
              {cat}
            </Badge>
          ))}
          <span className="text-slate-600 dark:text-slate-400">
            {name}
            {prayer.location ? ` · ${prayer.location}` : ''}
          </span>
        </div>

        <p className="whitespace-pre-wrap text-base text-slate-800 dark:text-slate-200">
          {prayer.body}
        </p>

        <CardFooter className="mt-4 justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={onSendToScreen}
            isLoading={isSending}
            disabled={isRemoving}
            className="text-green-600 hover:bg-green-50 hover:text-green-700 dark:text-green-400 dark:hover:bg-green-900/20"
            leftIcon={
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            }
          >
            Send to Screen
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemove}
            disabled={isRemoving || isSending}
            className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
            leftIcon={
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            }
          >
            Remove
          </Button>
        </CardFooter>
      </div>
    </Card>
  );
}

function StageQueueScreen({ token }: { token?: string }) {
  const {
    items,
    isLoading,
    error,
    sendingIds,
    removingIds,
    isClearing,
    sendToScreen,
    removeFromStage,
    clearStage,
  } = useStageQueue({ token, enabled: true });

  const [sort, setSort] = useState<StageSort>(readSort);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [confirmClear, setConfirmClear] = useState(false);

  const visibleItems = useMemo(() => {
    const filtered = items.filter((item) => {
      const praise = isPraise(item.prayer.type);
      if (typeFilter === 'prayer' && praise) return false;
      if (typeFilter === 'praise' && !praise) return false;
      if (categoryFilter !== 'all' && !getPrayerCategories(item.prayer).includes(categoryFilter)) {
        return false;
      }
      return true;
    });
    return [...filtered].sort((a, b) => {
      const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return sort === 'oldest' ? diff : -diff;
    });
  }, [items, sort, typeFilter, categoryFilter]);

  const handleSort = (value: string) => {
    const next: StageSort = value === 'newest' ? 'newest' : 'oldest';
    setSort(next);
    try {
      localStorage.setItem(SORT_STORAGE_KEY, next);
    } catch {
      // Ignore storage failures; the in-memory choice still applies.
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <PageHeader
        title="Stage"
        description="Prayers to read live on stage"
        actions={
          <Button
            variant="outline"
            onClick={() => setConfirmClear(true)}
            disabled={items.length === 0 || isClearing}
            isLoading={isClearing}
            className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/20"
          >
            Clear All
          </Button>
        }
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <Select
          label="Prayer type"
          value={typeFilter}
          onChange={(event) => setTypeFilter(event.target.value as TypeFilter)}
          options={[
            { value: 'all', label: 'All types' },
            { value: 'prayer', label: 'Prayer Request' },
            { value: 'praise', label: 'Answered Prayer' },
          ]}
        />
        <Select
          label="Category"
          value={categoryFilter}
          onChange={(event) => setCategoryFilter(event.target.value)}
          options={[
            { value: 'all', label: 'All categories' },
            ...CATEGORY_OPTIONS.map((option) => ({
              value: option.value,
              label: option.label,
            })),
          ]}
        />
        <SegmentedControl
          name="stage-sort"
          label="Sort"
          value={sort}
          onChange={handleSort}
          options={[
            { value: 'oldest', label: 'Oldest first' },
            { value: 'newest', label: 'Newest first' },
          ]}
        />
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2].map((key) => (
            <Card key={key} className="animate-pulse">
              <div className="h-6 w-32 rounded bg-slate-200 dark:bg-slate-700" />
              <div className="mt-4 h-16 rounded bg-slate-200 dark:bg-slate-700" />
            </Card>
          ))}
        </div>
      ) : visibleItems.length === 0 ? (
        <Card>
          <p className="py-8 text-center text-slate-500 dark:text-slate-400">
            {items.length === 0
              ? 'Nothing is on stage yet. Send a prayer from Submissions.'
              : 'No prayers match these filters.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {visibleItems.map((item) => {
            const prayerId = item.prayer.id;
            return (
              <StageCard
                key={item.id}
                item={item}
                isSending={sendingIds.has(prayerId)}
                isRemoving={removingIds.has(prayerId)}
                onSendToScreen={() => void sendToScreen(prayerId)}
                onRemove={() => void removeFromStage(prayerId)}
              />
            );
          })}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmClear}
        onClose={() => setConfirmClear(false)}
        onConfirm={() => {
          setConfirmClear(false);
          void clearStage();
        }}
        title="Clear the stage queue?"
        message="This removes every prayer from the stage screen. Their status stays Displayed."
        confirmLabel="Clear All"
        variant="danger"
        isLoading={isClearing}
      />
    </div>
  );
}

export default function StagePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { branding } = useBranding();
  const stageEnabled = branding.stageEnabled === true;
  const [token, setToken] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (user && !STAGE_ROLES.includes(user.role)) {
      navigate('/unauthorized', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const stored = localStorage.getItem('access_token') || undefined;
    setToken(stored);
  }, []);

  if (!stageEnabled) {
    return (
      <div className="mx-auto max-w-3xl">
        <PageHeader title="Stage" description="The stage tablet display is turned off" />
        <Card>
          <p className="text-slate-600 dark:text-slate-300">
            An admin can turn this on from Settings, on the Stage tab, with Enable tablet display.
          </p>
        </Card>
      </div>
    );
  }

  return <StageQueueScreen token={token} />;
}
